import type { User } from 'oidc-client-ts';

import type { ConsoleRole, ConsoleUser } from './types';

const ROLE_PRIORITY: ConsoleRole[] = ['PARTNER_ADMIN', 'FINANCE', 'DEVELOPER', 'VIEWER'];

interface ProfileClaims {
    sub: string;
    email?: string;
    given_name?: string;
    family_name?: string;
    preferred_username?: string;
    partner_id?: string;
    partner_name?: string;
}

interface RealmAccess {
    roles?: string[];
}

interface ExtendedProfile extends ProfileClaims {
    realm_access?: RealmAccess;
}

function resolveRole(profile: ExtendedProfile): ConsoleRole {
    const realmRoles = profile.realm_access?.roles ?? [];
    for (const candidate of ROLE_PRIORITY) {
        if (realmRoles.includes(candidate)) return candidate;
    }
    return 'VIEWER';
}

function buildInitials(profile: ExtendedProfile): string {
    const first = profile.given_name?.trim()[0] ?? '';
    const last = profile.family_name?.trim()[0] ?? '';
    const initials = (first + last).toUpperCase();
    if (initials.length > 0) return initials;
    const fallback = profile.preferred_username?.trim() ?? profile.email?.trim() ?? '?';
    return fallback.charAt(0).toUpperCase();
}

function buildFullName(profile: ExtendedProfile): string {
    const first = profile.given_name?.trim() ?? '';
    const last = profile.family_name?.trim() ?? '';
    const combined = `${first} ${last}`.trim();
    if (combined.length > 0) return combined;
    return profile.preferred_username ?? profile.email ?? 'Unknown user';
}

export function mapOidcUserToConsoleUser(user: User | null | undefined): ConsoleUser | null {
    if (!user || !user.profile) return null;
    const profile = user.profile as ExtendedProfile;
    return {
        id: profile.sub,
        email: profile.email ?? profile.preferred_username ?? '',
        fullName: buildFullName(profile),
        initials: buildInitials(profile),
        role: resolveRole(profile),
        partnerId: profile.partner_id ?? '',
        partnerName: profile.partner_name ?? '',
    };
}
